import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AccessDenied, Button, Input } from '@/presentation/components/common';
import { useAuth } from '@/presentation/contexts/AuthContext';
import { useToast } from '@/presentation/contexts/ToastContext';
import { httpClient } from '@/infrastructure/http/client/httpClient';
import {
  ClinicInvoiceModal,
  type ClinicInvoiceDTO,
} from '@/presentation/components/billing/ClinicInvoiceModal';
import { buildPrintHtml, formatPeriodLabel } from '@/presentation/components/billing/ClinicInvoiceDocument';
import { htmlToPdfBlob, invoicePdfFilename, saveBlob } from '@/shared/pdf/downloadHtmlPdf';

export type ClinicInvoiceListItem = {
  tenantId: string;
  nombre: string;
  estado: string;
  telefono: string | null;
  email: string | null;
  planName: string;
  planPrice: number;
  extraConsultorios: number;
  extraProfesionales: number;
  whatsappUsed: number;
  whatsappContracted: number;
  whatsappUsedPct: number;
  total: number;
  previousDebt: number;
  amountDue: number;
  isPreview: boolean;
  paymentStatus: string | null;
  invoiceStatus: 'suggested' | 'draft' | 'confirmed';
};

type ListResponse = {
  period: string;
  dueDate: string | null;
  items: ClinicInvoiceListItem[];
};

function previousPeriod(): string {
  const now = new Date();
  const d = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function currentPeriod(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

function shiftPeriod(period: string, delta: number): string {
  const [year, month] = period.split('-').map(Number);
  if (!year || !month) return previousPeriod();
  const d = new Date(year, month - 1 + delta, 1);
  const next = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  const max = currentPeriod();
  return next > max ? max : next;
}

function formatMoney(value: number): string {
  return `$ ${value.toLocaleString('es-AR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;
}

function isActiveClinic(estado: string): boolean {
  const value = estado.trim().toLowerCase();
  return value === 'active' || value === 'trial';
}

function periodChoices(locale: string, currentMonthLabel: string): { value: string; label: string }[] {
  const current = currentPeriod();
  const now = new Date();
  const choices: { value: string; label: string }[] = [];
  for (let i = 23; i >= 0; i -= 1) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const raw = formatPeriodLabel(value, locale);
    choices.push({
      value,
      label: value === current ? `${raw} (${currentMonthLabel})` : raw,
    });
  }
  return choices;
}

export const ClinicInvoicesPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { canAdminister, canOperateBilling } = useAuth();
  const { error: showError, success } = useToast();
  const [period, setPeriod] = useState(previousPeriod);
  const [clinicFilter, setClinicFilter] = useState<'active' | 'all'>('active');
  const [search, setSearch] = useState('');
  const [items, setItems] = useState<ClinicInvoiceListItem[]>([]);
  const [dueDate, setDueDate] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [exporting, setExporting] = useState<{ current: number; total: number } | null>(null);
  const [target, setTarget] = useState<{ tenantId: string; nombre: string } | null>(null);

  const canView = canAdminister || canOperateBilling;

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await httpClient.get<ListResponse>('/panel-pagos/facturas', {
        params: { periodo: period },
      });
      setItems(res.data.items ?? []);
      setDueDate(res.data.dueDate ?? null);
    } catch {
      showError(t('pages.clinicInvoices.loadError'));
    } finally {
      setIsLoading(false);
    }
  }, [period, showError, t]);

  useEffect(() => {
    if (canView) void load();
  }, [canView, load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((row) => {
      if (clinicFilter === 'active' && !isActiveClinic(row.estado)) return false;
      if (!q) return true;
      return row.nombre.toLowerCase().includes(q) || row.planName.toLowerCase().includes(q);
    });
  }, [items, search, clinicFilter]);

  const confirmedRows = useMemo(
    () =>
      items.filter((row) => {
        if (clinicFilter === 'active' && !isActiveClinic(row.estado)) return false;
        return row.invoiceStatus === 'confirmed';
      }),
    [items, clinicFilter]
  );

  const periodOptions = useMemo(
    () => periodChoices(i18n.language, t('pages.clinicInvoices.currentMonth')),
    [i18n.language, t]
  );

  const handleDownloadConfirmed = async () => {
    if (exporting || confirmedRows.length === 0) {
      showError(t('pages.clinicInvoices.downloadConfirmedNone'));
      return;
    }
    setExporting({ current: 0, total: confirmedRows.length });
    try {
      const JSZip = (await import('jszip')).default;
      const zip = new JSZip();
      let ok = 0;
      for (let i = 0; i < confirmedRows.length; i += 1) {
        const row = confirmedRows[i];
        setExporting({ current: i + 1, total: confirmedRows.length });
        try {
          const res = await httpClient.get<ClinicInvoiceDTO>(
            `/panel-pagos/factura/${encodeURIComponent(row.tenantId)}`,
            { params: { periodo: period } }
          );
          if (res.data.status !== 'confirmed') continue;
          const blob = await htmlToPdfBlob(buildPrintHtml(res.data, i18n.language, t));
          zip.file(invoicePdfFilename(res.data.nombre, res.data.period), blob);
          ok += 1;
        } catch {
          // sigue con el resto
        }
      }
      if (ok === 0) {
        showError(t('pages.clinicInvoices.downloadConfirmedNone'));
        return;
      }
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      saveBlob(zipBlob, `Facturas-${period}.zip`);
      if (ok < confirmedRows.length) {
        success(t('pages.clinicInvoices.downloadConfirmedPartial', { ok, total: confirmedRows.length, fail: confirmedRows.length - ok }));
      } else {
        success(t('pages.clinicInvoices.downloadConfirmedDone', { count: ok }));
      }
    } catch {
      showError(t('pages.clinicInvoices.exportError'));
    } finally {
      setExporting(null);
    }
  };

  if (!canView) {
    return <AccessDenied />;
  }

  return (
    <div className="panel-page">
      <div className="panel-page-header">
        <div className="min-w-0">
          <h1 className="panel-page-title">{t('pages.clinicInvoices.title')}</h1>
          <p className="panel-page-subtitle hidden sm:block">
            {t('pages.clinicInvoices.subtitle')}
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <div className="min-w-[12rem]">
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('pages.clinicInvoices.period')}
            </label>
            <div className="flex items-center gap-1">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                aria-label={t('pages.clinicInvoices.periodPrev')}
                onClick={() => setPeriod((p) => shiftPeriod(p, -1))}
              >
                ‹
              </Button>
              <select
                value={period}
                onChange={(e) => {
                  const next = e.target.value;
                  if (!/^\d{4}-\d{2}$/.test(next)) return;
                  setPeriod(next > currentPeriod() ? currentPeriod() : next);
                }}
                aria-label={t('pages.clinicInvoices.period')}
                className="h-8 min-w-[14rem] rounded-md border border-gray-300 bg-white px-2 text-sm text-slate-900 shadow-sm focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              >
                {periodOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                aria-label={t('pages.clinicInvoices.periodNext')}
                disabled={period >= currentPeriod()}
                onClick={() => setPeriod((p) => shiftPeriod(p, 1))}
              >
                ›
              </Button>
            </div>
            <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
              {t('pages.clinicInvoices.periodHint')}
            </p>
          </div>
          <Button variant="secondary" size="sm" onClick={() => void load()} disabled={isLoading}>
            {t('common.refresh')}
          </Button>
        </div>
      </div>

      <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        <div className="grid flex-1 grid-cols-1 gap-3 sm:grid-cols-2">
          <Input
            label={t('pages.clinicInvoices.search')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('pages.clinicInvoices.searchPlaceholder')}
          />
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('pages.clinicInvoices.clinicFilter')}
            </span>
            <select
              value={clinicFilter}
              onChange={(e) => setClinicFilter(e.target.value === 'all' ? 'all' : 'active')}
              className="h-10 w-full rounded-md border border-gray-300 bg-white px-3 text-sm text-slate-900 shadow-sm focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            >
              <option value="active">{t('pages.clinicInvoices.filterActive')}</option>
              <option value="all">{t('pages.clinicInvoices.filterAll')}</option>
            </select>
          </label>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {dueDate ? (
            <p className="text-sm text-slate-500">
              {t('pages.clinicInvoices.dueDate')}: {new Date(dueDate).toLocaleDateString()}
            </p>
          ) : null}
          <Button
            size="sm"
            onClick={() => void handleDownloadConfirmed()}
            disabled={isLoading || exporting !== null || confirmedRows.length === 0}
            isLoading={exporting !== null}
          >
            {exporting
              ? t('pages.clinicInvoices.downloadConfirmedProgress', {
                  current: exporting.current,
                  total: exporting.total,
                })
              : t('pages.clinicInvoices.downloadConfirmed', { count: confirmedRows.length })}
          </Button>
        </div>
      </div>

      <div className="panel-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:border-gray-700 dark:bg-gray-900/40 dark:text-slate-400">
                <th className="px-4 py-3">{t('pages.clinicInvoices.clinic')}</th>
                <th className="px-4 py-3">{t('pages.clinicInvoices.plan')}</th>
                <th className="px-4 py-3 text-right">{t('pages.clinicInvoices.extrasShort')}</th>
                <th className="px-4 py-3 text-right">{t('pages.clinicInvoices.whatsappShort')}</th>
                <th className="px-4 py-3 text-right">{t('pages.clinicInvoices.invoiceTotal')}</th>
                <th className="px-4 py-3">{t('pages.clinicInvoices.status')}</th>
                <th className="px-4 py-3 text-right">{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-gray-800">
              {isLoading ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-slate-500">
                    {t('common.loading')}
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-slate-500">
                    {t('pages.clinicInvoices.empty')}
                  </td>
                </tr>
              ) : (
                filtered.map((row) => (
                  <tr key={row.tenantId} className="hover:bg-slate-50/80 dark:hover:bg-gray-900/30">
                    <td className="px-4 py-3">
                      <p className="font-medium text-slate-900 dark:text-white">{row.nombre}</p>
                      <p className="text-xs capitalize text-slate-500">{row.estado}</p>
                    </td>
                    <td className="px-4 py-3">
                      <p>{row.planName}</p>
                      <p className="text-xs text-slate-500">{formatMoney(row.planPrice)}</p>
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">
                      {row.extraConsultorios}c · {row.extraProfesionales}p
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">
                      {row.whatsappUsed.toLocaleString()}/{row.whatsappContracted.toLocaleString()}
                      <span className="ml-1 text-xs text-slate-500">
                        ({row.whatsappUsedPct.toFixed(0)}%)
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums font-semibold">
                      {formatMoney(row.total)}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium ${
                          row.invoiceStatus === 'confirmed'
                            ? 'bg-teal-50 text-teal-800 dark:bg-teal-900/30 dark:text-teal-200'
                            : 'bg-amber-50 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200'
                        }`}
                      >
                        {row.invoiceStatus === 'confirmed'
                          ? t('pages.clinicInvoices.confirmedBadge')
                          : row.invoiceStatus === 'draft'
                            ? t('pages.clinicInvoices.draftBadge')
                            : t('pages.clinicInvoices.suggestedBadge')}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => setTarget({ tenantId: row.tenantId, nombre: row.nombre })}
                      >
                        {t('pages.clinicInvoices.generate')}
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {target ? (
        <ClinicInvoiceModal
          tenantId={target.tenantId}
          period={period}
          clinicName={target.nombre}
          onClose={() => setTarget(null)}
          onChanged={() => void load()}
        />
      ) : null}
    </div>
  );
};

